<template>
  <SearchField
    v-if="contactCapture?.isFieldVisible(name) && searchable"
    v-bind="$attrs"
    label-key="address"
    value-key="id"
    :helpText="helpText"
    :id="id"
    :invalidFeedback="invalidFeedback"
    :label="label"
    :loading="isLoading"
    :model-value="postcode"
    :options="addressItems"
    :required="required"
    :validFeedback="validFeedback"
    @search="onSearch"
    @select="onSelect"
    @update:model-value="onChange"
  />
  <TextField
    v-else-if="contactCapture?.isFieldVisible(name)"
    v-bind="$attrs"
    :helpText="helpText"
    :id="id"
    :invalidFeedback="invalidFeedback"
    :label="label"
    :model-value="postcode"
    :required="required"
    :validFeedback="validFeedback"
    @update:model-value="onChange"
  />
</template>

<script lang="ts">
export type PostcodeProps = Omit<SearchFieldProps, "options">;
</script>

<script lang="ts" setup>
import { useContactCaptureContext } from "@/components/forms/useContactCapture";
import { AddressSearchItem, searchAddress } from "@/lib/searchAddress";
import { computed, ref } from "vue";
import SearchField, { Option, SearchFieldProps } from "./SearchField.vue";
import TextField from "./TextField.vue";

withDefaults(defineProps<PostcodeProps>(), {
  name: "postcode",
});

const contactCapture = useContactCaptureContext();

const addressItems = ref<AddressSearchItem[]>([]);
const isLoading = ref<boolean>(false);

const postcode = computed<string | null>({
  get: () => contactCapture?.postcode.value ?? null,
  set: (newValue) => {
    if (contactCapture) {
      contactCapture.postcode.value = newValue;
    }
  },
});

const searchable = computed<boolean>(() => contactCapture?.country.value === 1);

function onChange(value: string | null) {
  postcode.value = value?.toUpperCase() ?? null;
  addressItems.value = [];
}

async function onSearch() {
  addressItems.value = [];
  isLoading.value = true;

  if (postcode.value?.length) {
    try {
      addressItems.value = await searchAddress(postcode.value);
    } catch (error) {
      console.error("Error while fetching addresses.", error);
    } finally {
      isLoading.value = false;
    }
  }
}

function onSelect(item: Option) {
  if (contactCapture) {
    contactCapture.address.value = (<AddressSearchItem>item).address;
    addressItems.value = [];
  }
}
</script>
